/**
 * checklist.ts — regras de checklist compartilhadas entre Ronda, O.S., Enquete e a tela
 * de Checklists (gatilho livre / ponto eletrônico).
 *
 * Antes deste módulo o mapeamento "template da API → ChecklistSchema" estava duplicado em
 * OrdemServico, Ronda e Enquete, cada cópia com um subconjunto diferente das regras do
 * TirvuAdm (máscara, seção condicionada, preenchimento posterior). Aqui existe UMA cópia.
 */

import type {
  ChecklistFieldSchema,
  ChecklistOption,
  ChecklistSchema,
  ChecklistValues,
} from "@/components/checklist/ChecklistBottomSheet";

// ─── Gatilho de execução ──────────────────────────────────────────────────────
// Contrato do TirvuAdm: tab_adm_inspecoes_cadastro_gatilhos guarda um JSON por linha na
// coluna `descricao` — {"Evento":"PONTO_ELETRONICO","Descricao":"SAIDA"}. Um checklist SEM
// nenhuma linha ativa é de gatilho LIVRE. O t-credentials já entrega isso parseado em
// `gatilhos[]` + `gatilhoLivre`; aqui reparseamos defensivamente para o app continuar
// funcionando contra um backend ainda não redeployado.

export type ChecklistGatilho = {
  evento: string;
  descricao: string;
};

/** Tipos de batida do ponto — mesmo enum que o Registro usa (id_rh_ponto_tipo 1..4). */
export type TipoBatidaPonto =
  "ENTRADA" | "SAIDA_PAUSA" | "RETORNO_PAUSA" | "SAIDA";

export const EVENTO_PONTO_ELETRONICO = "PONTO_ELETRONICO";

/** id_rh_ponto_tipo → tipo de batida. Espelha a numeração usada em Registro.tsx. */
export function tipoBatidaPorIdPontoTipo(id: any): TipoBatidaPonto | null {
  switch (Number(id)) {
    case 1:
      return "ENTRADA";
    case 2:
      return "SAIDA_PAUSA";
    case 3:
      return "RETORNO_PAUSA";
    case 4:
      return "SAIDA";
    default:
      return null;
  }
}

// Combining diacritics (U+0300..U+036F), montado por escape em string: o range escrito
// direto no literal do regex fica invisível no editor e não sobrevive a um reencode.
const DIACRITICOS = new RegExp("[\\u0300-\\u036f]", "g");

/** MAIÚSCULAS, sem acento, separadores virando "_". "Saída p/ Intervalo" → "SAIDA_P_INTERVALO". */
function normalizarChave(raw: any): string {
  return String(raw ?? "")
    .normalize("NFD")
    .replace(DIACRITICOS, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Descrição do gatilho → tipo de batida. Tolerante de propósito: o TirvuAdm pode gravar
 * SAIDA_INTERVALO, SAIDA_ALMOCO, RETORNO_INTERVALO, VOLTA_ALMOCO… Casamos por radical em
 * vez de por enum exato, então uma nomenclatura nova não silencia o gatilho.
 *
 * Ordem importa: RETORNO/VOLTA antes de SAIDA (senão "RETORNO_INTERVALO" nunca chegaria
 * ao seu ramo), e a saída de pausa antes da saída final.
 */
export function tipoBatidaDoGatilho(descricao: any): TipoBatidaPonto | null {
  const k = normalizarChave(descricao);
  if (!k) return null;

  if (k.includes("RETORNO") || k.includes("VOLTA")) return "RETORNO_PAUSA";
  if (
    k.includes("SAIDA") &&
    (k.includes("INTERVALO") || k.includes("ALMOCO") || k.includes("PAUSA"))
  ) {
    return "SAIDA_PAUSA";
  }
  if (k.includes("SAIDA") || k.includes("FIM_JORNADA")) return "SAIDA";
  if (k.includes("ENTRADA") || k.includes("INICIO_JORNADA")) return "ENTRADA";
  return null;
}

/** Lê os gatilhos de um template, aceitando o JSON cru caso o backend não tenha parseado. */
export function gatilhosDoTemplate(template: any): ChecklistGatilho[] {
  const raw = template?.gatilhos ?? template?.Gatilhos;
  if (!Array.isArray(raw)) return [];

  const out: ChecklistGatilho[] = [];
  for (const item of raw) {
    if (item == null) continue;
    let obj: any = item;
    // Backend antigo/cru: a linha pode chegar como a própria string JSON da coluna.
    if (typeof obj === "string") {
      try {
        obj = JSON.parse(obj);
      } catch {
        continue;
      }
    }
    if (typeof obj !== "object") continue;
    const evento = normalizarChave(obj.evento ?? obj.Evento);
    const descricao = String(obj.descricao ?? obj.Descricao ?? "").trim();
    if (!evento && !descricao) continue;
    out.push({ evento, descricao });
  }
  return out;
}

/**
 * Gatilho LIVRE = nenhum gatilho ativo cadastrado. Confia no `gatilhoLivre` do backend
 * quando ele vem; senão deriva da lista (é a MESMA regra, só calculada de onde der).
 */
export function isChecklistLivre(template: any): boolean {
  const flag = template?.gatilhoLivre ?? template?.GatilhoLivre;
  if (typeof flag === "boolean") return flag;
  return gatilhosDoTemplate(template).length === 0;
}

/**
 * O colaborador recebeu este checklist (tab_adm_inspecoes_envio_colaboradores)?
 *
 * Ausência do campo = NÃO permitido. É o default seguro: os checklists de Ronda/O.S.
 * também não têm gatilho, então seriam lidos como "livres" e apareceriam no menu como
 * avulsos. Contra um backend ainda sem o campo, o recurso simplesmente não liga — o que
 * é preferível a listar checklist de ronda como execução avulsa.
 */
export function isChecklistPermitido(template: any): boolean {
  const flag = template?.permitido ?? template?.Permitido;
  if (typeof flag === "boolean") return flag;
  return flag === "S" || flag === 1 || flag === "1";
}

/** Checklists que o colaborador executa por conta própria (aba Checklists do menu). */
export function checklistsLivres(templates: any[] | null | undefined): any[] {
  return (Array.isArray(templates) ? templates : []).filter(
    (t) => isChecklistPermitido(t) && isChecklistLivre(t),
  );
}

/**
 * Checklists que precisam ser respondidos ANTES de bater o ponto do tipo informado.
 * Gatilho ilegível não dispara nada: travar a batida por causa de um JSON corrompido no
 * cadastro seria pior que deixar o checklist passar (o colaborador ficaria sem registrar).
 */
export function checklistsDaBatida(
  templates: any[] | null | undefined,
  tipo: TipoBatidaPonto | null,
): any[] {
  if (!tipo) return [];
  return (Array.isArray(templates) ? templates : []).filter((t) => {
    if (!isChecklistPermitido(t)) return false;
    return gatilhosDoTemplate(t).some((g) => {
      if (g.evento && g.evento !== EVENTO_PONTO_ELETRONICO) return false;
      return tipoBatidaDoGatilho(g.descricao) === tipo;
    });
  });
}

/**
 * Instante ATUAL como "YYYY-MM-DDTHH:mm:ss" na hora LOCAL, sem sufixo de fuso.
 *
 * Não use toISOString() aqui: ele emite UTC ("...Z"), e o T-Consumer grava o campo com
 * `{TempoInicio:yyyy-MM-dd HH:mm:ss}` — a hora LOCAL do servidor. Com o "Z" os dígitos
 * gravados divergem dos que o app guardou, e a reconciliação (que casa execução local
 * com a do servidor por checklist + tempo_inicio) nunca encontraria o par — o selo
 * ficaria eternamente em 2 checks cinza. Mandando hora local sem fuso, o .NET lê como
 * Unspecified e grava exatamente os mesmos dígitos. Mesma convenção do data_log do ponto.
 */
export function agoraLocalSemFuso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

// ─── Template → ChecklistSchema ───────────────────────────────────────────────

/** tipo_resposta do cadastro → tipo de campo do ChecklistBottomSheet. */
export function mapChecklistTipoResposta(
  raw: any,
): ChecklistFieldSchema["type"] {
  const tipo = String(raw ?? "").trim();
  const tipoUpper = tipo.toUpperCase();

  const porCodigo: Record<string, ChecklistFieldSchema["type"]> = {
    T: "texto",
    N: "numero",
    D: "data",
    H: "hora",
    LOS: "lista_unica",
    LOM: "lista_multipla",
    RANGE: "range",
    RNG: "range",
    F: "foto",
    A: "assinatura",
    PDF: "pdf",
    DOWN: "download",
    M: "monetario",
    TEL: "telefone",
    C: "colaborador",
    V: "veiculo",
    P: "posto_servico",
    FRE: "freelancer",
    LSP: "lsp",
  };
  if (porCodigo[tipoUpper]) return porCodigo[tipoUpper];

  // O t-credentials já converte o código para o nome do tipo — aceitamos os dois.
  const jaConvertidos: ChecklistFieldSchema["type"][] = [
    "texto",
    "numero",
    "data",
    "hora",
    "lista_unica",
    "lista_multipla",
    "download",
    "range",
    "foto",
    "assinatura",
    "pdf",
    "monetario",
    "telefone",
    "colaborador",
    "veiculo",
    "posto_servico",
    "freelancer",
    "lsp",
  ];
  if (jaConvertidos.includes(tipo as any)) return tipo as any;
  return "texto";
}

/**
 * Seção de "preenchimento posterior": não aparece para o colaborador — quem responde é o
 * TirvuAdm depois. Aceita as duas grafias porque o campo trafega camelCase pelo
 * t-credentials e snake_case quando vem cru do banco.
 */
export function isSecaoPreenchimentoPosterior(secao: any): boolean {
  return (
    secao?.preenchimentoPosterior === true ||
    secao?.preenchimentoPosterior === "S" ||
    secao?.preenchimento_posterior === true ||
    secao?.preenchimento_posterior === "S"
  );
}

function isVerdadeiro(v: any): boolean {
  return v === true || v === "S" || v === 1 || v === "1";
}

export type ChecklistSchemaDeps = {
  pessoas?: any[] | null;
  postos?: any[] | null;
  veiculos?: any[] | null;
};

/**
 * Injeta as opções dos campos que se alimentam das listas do app (colaborador, posto,
 * veículo, freelancer). Aceita PascalCase e camelCase porque as listas vêm ora do T-ARC,
 * ora do t-credentials.
 */
function injetarOpcoesDeDominio(
  field: any,
  { pessoas, postos, veiculos }: ChecklistSchemaDeps,
): any {
  const up = (v: any) => String(v ?? "").toUpperCase();

  if (field.type === "colaborador" && pessoas) {
    field.options = pessoas
      .filter((p: any) => up(p.Tipo ?? p.tipo ?? p.tipo_colaborador) === "C")
      .map((p: any) => ({
        label: up(p.Nome ?? p.nome),
        value: String(p.Id ?? p.id ?? ""),
        subtitle: (p.Cargo ?? p.cargo) ? up(p.Cargo ?? p.cargo) : undefined,
      }));
  } else if (field.type === "posto_servico" && postos) {
    field.options = postos.map((p: any) => ({
      label: up(p.Nome ?? p.apelido ?? p.nome),
      value: String(p.Id ?? p.id ?? p.idPosto ?? ""),
      subtitle:
        (p.nome_razao ?? p.nomeRazao)
          ? up(p.nome_razao ?? p.nomeRazao)
          : undefined,
    }));
  } else if (field.type === "veiculo" && veiculos) {
    field.options = veiculos.map((v: any) => ({
      label: `${up(v.Placa ?? v.placa)} - ${up(v.Modelo ?? v.modelo)}`.trim(),
      value: String(v.Id ?? v.id ?? ""),
      subtitle: (v.Nome ?? v.nome) ? up(v.Nome ?? v.nome) : undefined,
    }));
  } else if (field.type === "freelancer" && pessoas) {
    field.options = pessoas
      .filter((p: any) => {
        const t = up(p.Tipo ?? p.tipo);
        return (
          t === "F" || t === "FRE" || up(p.Cargo ?? p.cargo).includes("FREELA")
        );
      })
      .map((p: any) => ({
        label: up(p.Nome ?? p.nome),
        value: String(p.Id ?? p.id ?? ""),
        subtitle: (p.Cargo ?? p.cargo) ? up(p.Cargo ?? p.cargo) : undefined,
      }));
  }

  return field;
}

/**
 * Injeta as opções de domínio (colaborador / posto / veículo / freelancer) num schema
 * ESTÁTICO, montado à mão em vez de vir do cadastro. Usado pelo fallback da O.S., que
 * exibe um checklist fixo quando a ordem não tem template vinculado.
 */
export function aplicarOpcoesDeDominio(
  schema: ChecklistSchema,
  deps: ChecklistSchemaDeps = {},
): ChecklistSchema {
  return {
    ...schema,
    sections: (schema.sections ?? []).map((secao) => ({
      ...secao,
      fields: (secao.fields ?? []).map((f) =>
        injetarOpcoesDeDominio({ ...f }, deps),
      ),
    })),
  };
}

/**
 * Template do cadastro → schema do ChecklistBottomSheet, aplicando as regras do
 * administrativo: seções de preenchimento posterior somem, seções condicionadas viajam
 * com suas condições, perguntas condicionadas viram `dependsOn` e a máscara de texto
 * configurada no TirvuAdm é repassada.
 */
export function buildChecklistSchema(
  template: any,
  deps: ChecklistSchemaDeps = {},
  overrides: {
    title?: string;
    description?: string;
    /**
     * Sobe o enunciado da pergunta para MAIÚSCULAS. Enquete e a tela de Checklists usam
     * true; Ronda e O.S. sempre mostraram o texto como cadastrado e passam false — é só
     * apresentação, e unificar o mapper não é motivo para mudar a cara das telas.
     */
    labelMaiusculo?: boolean;
    /**
     * Quais seções entram: "normais" (padrão) é a execução do colaborador, que pula o
     * preenchimento posterior; "complementares" é o inverso, usado para responder DEPOIS
     * só a parte complementar, sem reabrir o que já foi respondido.
     */
    escopoSecoes?: "normais" | "complementares";
  } = {},
): ChecklistSchema | null {
  if (!template) return null;

  const labelMaiusculo = overrides.labelMaiusculo !== false;
  const soComplementares = overrides.escopoSecoes === "complementares";

  const secoes = Array.isArray(template.secoes)
    ? template.secoes
    : Array.isArray(template.Secoes)
      ? template.Secoes
      : [];

  const schema: ChecklistSchema = {
    id: String(template.id ?? template.Id ?? ""),
    title:
      overrides.title ??
      String(
        template.descricao ?? template.Descricao ?? "CHECKLIST",
      ).toUpperCase(),
    description:
      overrides.description ??
      "Preencha os campos abaixo para concluir o checklist.",
    sections: secoes
      .filter((secao: any) =>
        soComplementares
          ? isSecaoPreenchimentoPosterior(secao)
          : !isSecaoPreenchimentoPosterior(secao),
      )
      .map((secao: any) => ({
        id: String(secao.id ?? secao.Id ?? secao.ordem ?? ""),
        title: String(secao.descricao ?? secao.Descricao ?? "").toUpperCase(),
        condicionada: isVerdadeiro(secao.condicionada ?? secao.Condicionada),
        condicoes: secao.condicoes ?? secao.Condicoes ?? null,
        fields: (secao.perguntas ?? secao.Perguntas ?? []).map(
          (pergunta: any) => {
            const enunciado = String(
              pergunta.pergunta ?? pergunta.Pergunta ?? "",
            );
            const field: any = {
              id: String(pergunta.id ?? pergunta.Id),
              label: labelMaiusculo ? enunciado.toUpperCase() : enunciado,
              type: mapChecklistTipoResposta(
                pergunta.tipo ??
                  pergunta.tipoResposta ??
                  pergunta.tipo_resposta,
              ),
              required: isVerdadeiro(
                pergunta.obrigatorio ?? pergunta.Obrigatorio,
              ),
              placeholder: "Preencha aqui...",
            };

            if (field.type === "range") {
              field.min = typeof pergunta.min === "number" ? pergunta.min : 1;
              field.max = typeof pergunta.max === "number" ? pergunta.max : 10;
              field.step =
                typeof pergunta.step === "number" ? pergunta.step : 1;
            }

            // Máscara (CPF, CNPJ, placa…) só se aplica a texto livre.
            if (
              field.type === "texto" &&
              isVerdadeiro(pergunta.mascaraAtiva ?? pergunta.mascara_ativa) &&
              (pergunta.mascaraTipo ?? pergunta.mascara_tipo)
            ) {
              field.maskType = String(
                pergunta.mascaraTipo ?? pergunta.mascara_tipo,
              ).toUpperCase();
            }

            // Valor padrão (TirvuAdm): quando ativo, pré-preenche a resposta numa execução
            // NOVA. Guardado como TEXTO cru; a conversão por tipo/máscara/opção é no sheet
            // (valorPadraoInicial). Aceita camelCase (t-credentials) e snake_case (cru do banco).
            if (isVerdadeiro(pergunta.temValorPadrao ?? pergunta.tem_valor_padrao)) {
              field.temValorPadrao = true;
              field.valorPadrao = String(
                pergunta.valorPadrao ?? pergunta.valor_padrao ?? "",
              );
            }

            // Pergunta condicionada: só aparece quando a pergunta-referência tem a
            // resposta esperada. É a "resposta esperada" do cadastro.
            // Aceita as duas grafias porque as tabelas de checklist e de enquete nomeiam
            // essas colunas de formas diferentes e o backend já misturou as duas.
            const refCondicionada =
              pergunta.idCondicionadaRef ??
              pergunta.IdCondicionadaRef ??
              pergunta.id_condicionada_ref ??
              pergunta.id_inspecao_cadastro_checklist_condicionada_ref;
            if (isVerdadeiro(pergunta.condicionada) && refCondicionada) {
              field.dependsOn = {
                fieldId: String(refCondicionada),
                value:
                  pergunta.condicionadaRespostaEsperada ??
                  pergunta.CondicionadaRespostaEsperada ??
                  pergunta.condicionada_resposta_esperada ??
                  pergunta.resposta_condicionada_esperada,
              };
            }

            const itens = pergunta.lista?.itens ?? pergunta.Lista?.Itens;
            if (Array.isArray(itens)) {
              field.options = itens.map((item: any) => ({
                label: String(
                  item.descricao ?? item.Descricao ?? "",
                ).toUpperCase(),
                value: String(
                  item.id ?? item.Id ?? item.value ?? item.descricao ?? "",
                ),
              }));
            }

            return injetarOpcoesDeDominio(field, deps);
          },
        ),
      })),
  };

  resolverValoresCondicionados(schema);
  return schema;
}

/**
 * Segunda passada sobre o schema: converte a resposta esperada das perguntas
 * condicionadas no `value` da alternativa correspondente.
 *
 * Por que é necessário: o TirvuAdm grava na condicional o TEXTO da alternativa ("Sim"),
 * mas a resposta que o app guarda é o `value` da opção, que é o ID do item da lista. O
 * sheet até tenta resolver texto→value sozinho, só que o faz com comparação SENSÍVEL a
 * caixa (`normalizeValue` = String().trim()) — e os rótulos aqui saem em MAIÚSCULAS. Com
 * isso "Sim" nunca casava com o rótulo "SIM", a resolução falhava e a pergunta
 * condicionada ficava permanentemente visível.
 *
 * Resolvendo aqui, onde temos a pergunta-referência e suas opções, a comparação do sheet
 * passa a ser value×value — exata, sem depender de caixa nem de acento.
 */
function resolverValoresCondicionados(schema: ChecklistSchema): void {
  const opcoesPorCampo = new Map<string, ChecklistOption[]>();
  for (const secao of schema.sections ?? []) {
    for (const campo of secao.fields ?? []) {
      if (campo.options?.length)
        opcoesPorCampo.set(String(campo.id), campo.options);
    }
  }
  if (opcoesPorCampo.size === 0) return;

  // Texto → value da alternativa. Sem opção correspondente, devolve o texto original:
  // a referência pode ser uma pergunta de texto livre, em que comparar cru é o certo.
  const resolver = (bruto: any, opcoes?: ChecklistOption[]): any => {
    if (bruto == null || !opcoes?.length) return bruto;
    const alvo = normalizarChave(bruto);
    if (!alvo) return bruto;
    const achou = opcoes.find(
      (o) =>
        normalizarChave(o.value) === alvo || normalizarChave(o.label) === alvo,
    );
    return achou ? achou.value : bruto;
  };

  for (const secao of schema.sections ?? []) {
    for (const campo of secao.fields ?? []) {
      const dep = campo.dependsOn;
      if (!dep) continue;
      const opcoes = opcoesPorCampo.get(String(dep.fieldId));
      if (!opcoes) continue;
      dep.value = Array.isArray(dep.value)
        ? dep.value.map((v) => resolver(v, opcoes))
        : resolver(dep.value, opcoes);
    }
  }
}

// ─── Seção complementar e perguntas chave ─────────────────────────────────────

/** Perguntas de uma seção, aceitando as duas grafias. */
function perguntasDaSecao(secao: any): any[] {
  const p = secao?.perguntas ?? secao?.Perguntas;
  return Array.isArray(p) ? p : [];
}

/** Seções do template, aceitando as duas grafias. */
function secoesDoTemplate(template: any): any[] {
  const s = template?.secoes ?? template?.Secoes;
  return Array.isArray(s) ? s : [];
}

/**
 * O checklist tem seção COMPLEMENTAR (preenchimento posterior)?
 *
 * Essas seções não vão para o colaborador: a execução do app cobre só as seções normais
 * e a complementar é respondida depois, por escolha, no módulo de checklist do TirvuAdm.
 * Serve para a tela avisar que aquela execução ainda tem parte pendente lá.
 */
export function temSecaoComplementar(template: any): boolean {
  return secoesDoTemplate(template).some(isSecaoPreenchimentoPosterior);
}

/**
 * Ids das perguntas que vivem nas seções complementares. Servem para saber se o
 * complemento já foi respondido (basta o servidor ter resposta para alguma delas) e para
 * carimbar o rastro local do envio.
 */
export function perguntaIdsComplementares(template: any): number[] {
  const out: number[] = [];
  for (const secao of secoesDoTemplate(template)) {
    if (!isSecaoPreenchimentoPosterior(secao)) continue;
    for (const p of perguntasDaSecao(secao)) {
      const id = Number(p?.id ?? p?.Id);
      if (Number.isFinite(id)) out.push(id);
    }
  }
  return out;
}

export type ResumoPerguntaChave = {
  perguntaId: string;
  pergunta: string;
  resposta: string;
};

/**
 * Perguntas marcadas como CHAVE no cadastro, com a resposta da execução — é o que o grid
 * do ADM destaca em badge e o que a busca da tela precisa varrer.
 *
 * A resposta gravada de uma pergunta de lista é o ID do item; aqui devolvemos o TEXTO,
 * que é o que faz sentido ler num badge e digitar numa busca. Pergunta chave sem resposta
 * (ex.: a que vive na seção complementar, ainda não respondida) fica de fora.
 */
export function resumoPerguntasChave(
  template: any,
  respostas: any[] | null | undefined,
): ResumoPerguntaChave[] {
  if (!template) return [];

  const porPergunta = new Map<string, any>();
  for (const r of Array.isArray(respostas) ? respostas : []) {
    const pid =
      r?.perguntaId ?? r?.PerguntaId ?? r?.id_inspecao_cadastro_checklist;
    if (pid == null) continue;
    porPergunta.set(
      String(pid),
      r?.resposta ?? r?.Resposta ?? r?.texto_resposta,
    );
  }
  if (porPergunta.size === 0) return [];

  const out: ResumoPerguntaChave[] = [];
  for (const secao of secoesDoTemplate(template)) {
    for (const p of perguntasDaSecao(secao)) {
      if (!isVerdadeiro(p?.perguntaChave ?? p?.pergunta_chave)) continue;

      const perguntaId = String(p?.id ?? p?.Id ?? "");
      const bruto = porPergunta.get(perguntaId);
      if (bruto == null || String(bruto).trim() === "") continue;

      // Resposta de lista vem como id do item → troca pelo texto da alternativa.
      // Multivalorada ("501, 502") é resolvida item a item.
      const itens = p?.lista?.itens ?? p?.Lista?.Itens;
      let texto = String(bruto);
      if (Array.isArray(itens) && itens.length) {
        const rotulo = (v: string) => {
          const alvo = normalizarChave(v);
          const item = itens.find(
            (i: any) =>
              normalizarChave(i?.id ?? i?.Id) === alvo ||
              normalizarChave(i?.descricao ?? i?.Descricao) === alvo,
          );
          return item ? String(item.descricao ?? item.Descricao ?? v) : v;
        };
        texto = texto
          .split(",")
          .map((parte) => rotulo(parte.trim()))
          .filter((parte) => parte !== "")
          .join(", ");
      }

      out.push({
        perguntaId,
        pergunta: String(p?.pergunta ?? p?.Pergunta ?? ""),
        resposta: texto,
      });
    }
  }
  return out;
}

/** Todos os campos do schema, achatando as seções. */
export function camposDoSchema(
  schema: ChecklistSchema | null,
): ChecklistFieldSchema[] {
  if (!schema) return [];
  return schema.sections
    ? schema.sections.flatMap((s) => s.fields || [])
    : (schema.fields ?? []);
}

// ─── Values → payload do ChecklistExecucao ────────────────────────────────────

export type ChecklistRespostaPayload = {
  PerguntaId: number;
  Resposta: string;
  Foto?: string;
};

export type ChecklistAssetPayload = {
  PerguntaId: number;
  Tipo: "foto" | "assinatura" | "pdf" | "lsp";
  Uri: string;
};

/**
 * Converte as respostas do sheet no formato que o SyncService.checklistSalvar espera.
 * Mesma conversão que a Ronda já fazia inline — inclusive o caso LSP, em que a foto vai
 * como asset com a MESMA PerguntaId da placa para o Sync juntar a URL na resposta.
 */
export function buildChecklistRespostas(
  schema: ChecklistSchema | null,
  values: ChecklistValues,
): { respostas: ChecklistRespostaPayload[]; assets: ChecklistAssetPayload[] } {
  const respostas: ChecklistRespostaPayload[] = [];
  const assets: ChecklistAssetPayload[] = [];

  for (const field of camposDoSchema(schema)) {
    const value: any = (values as any)[field.id];
    if (value === undefined || value === null) continue;

    const perguntaId = parseInt(field.id, 10);
    if (!Number.isFinite(perguntaId)) continue;

    if (field.type === "foto" && Array.isArray(value)) {
      for (const asset of value) {
        if (asset && "uri" in asset && asset.uri) {
          assets.push({ PerguntaId: perguntaId, Tipo: "foto", Uri: asset.uri });
        }
      }
    } else if (
      (field.type === "assinatura" || field.type === "pdf") &&
      value &&
      typeof value === "object" &&
      "uri" in value &&
      (value as any).uri
    ) {
      assets.push({
        PerguntaId: perguntaId,
        Tipo: field.type,
        Uri: String((value as any).uri),
      });
    } else if (field.type === "lsp") {
      if (typeof value === "string" || typeof value === "number") {
        respostas.push({ PerguntaId: perguntaId, Resposta: String(value) });
      }
      const fotoLsp = (values as any)[`__lspfoto_${field.id}`];
      if (fotoLsp && typeof fotoLsp === "string") {
        assets.push({ PerguntaId: perguntaId, Tipo: "lsp", Uri: fotoLsp });
      }
    } else if (Array.isArray(value)) {
      const normalizado = value
        .map((v) => (v == null ? "" : String(v)))
        .filter((v) => v.trim() !== "");
      if (normalizado.length > 0) {
        respostas.push({
          PerguntaId: perguntaId,
          Resposta: normalizado.join(", "),
        });
      }
    } else if (typeof value === "string" || typeof value === "number") {
      if (String(value).trim() !== "") {
        respostas.push({ PerguntaId: perguntaId, Resposta: String(value) });
      }
    }
  }

  return { respostas, assets };
}

/** Linha que carrega mídia (foto anexada, foto do LSP, assinatura, PDF). */
export function ehLinhaDeMidia(r: any): boolean {
  if (r?.foto ?? r?.Foto) return true;
  const v = String(
    r?.resposta ?? r?.Resposta ?? r?.texto_resposta ?? "",
  ).trim();
  return /^(https?:\/\/|file:|content:|data:)/i.test(v);
}

/**
 * Sobrepõe às respostas do SERVIDOR as que foram gravadas neste aparelho e ainda não
 * voltaram de lá (edição ou complemento a caminho).
 *
 * Por que existe: `200` do T-ARC significa ENFILEIRADO, não gravado. Entre salvar e o
 * T-Consumer escrever no banco, a execução do servidor ainda traz o texto ANTIGO — e é
 * dele que sai o resumo do card. Sem esta sobreposição o card fica mentindo até o próximo
 * refresh, mesmo com a edição já salva aqui.
 *
 * ⚠️ Só substitui pergunta respondida em TEXTO. Foto viaja como asset e só vira linha
 * depois do upload, então a linha de mídia do servidor é preservada — inclusive na MESMA
 * pergunta (caso LSP: texto da placa + foto). Sem isso, editar a placa apagaria a foto da
 * tela até sincronizar.
 */
/**
 * Chave NATURAL de uma execução de checklist: `checklistId|YYYYMMDDHHMMSS`.
 *
 * O `Salvar` NÃO devolve o id da execução (200 do T-ARC = enfileirado no Rabbit), então
 * esta é a única forma de casar o que foi executado NO APARELHO com o registro que volta
 * do SERVIDOR. O `tempo_inicio` é gerado pelo app e gravado literal pelo T-Consumer, o que
 * o torna estável até entre reenvios da fila.
 *
 * ⚠️ Tem que ser a MESMA função nos três lugares que dependem dela (reconciliação,
 * gravação do complemento pendente e o card do servidor) — se divergirem, o complemento
 * fica órfão e o botão "Responder complementar" volta a assombrar.
 * Aceita "2026-08-14T12:34:56.789Z" e "2026-08-14 12:34:56" como a mesma coisa.
 */
export function chaveNaturalExecucao(
  checklistId: any,
  tempoInicio: any,
): string {
  const t = String(tempoInicio ?? "").trim();
  if (!t) return "";
  const m = /(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/.exec(t);
  if (!m) return "";
  return `${Number(checklistId) || 0}|${m[1]}${m[2]}${m[3]}${m[4]}${m[5]}${m[6]}`;
}

export function mesclarRespostasLocais(
  servidor: any[] | null | undefined,
  // `Resposta` opcional porque o rastro local guarda o payload já gravado, onde uma
  // linha pode ser só de mídia.
  locais:
    | { PerguntaId: number; Resposta?: string; Foto?: string }[]
    | null
    | undefined,
): any[] {
  const base = Array.isArray(servidor) ? servidor : [];
  const overlay = Array.isArray(locais) ? locais : [];
  if (overlay.length === 0) return base;

  const substituidas = new Set<string>();
  for (const l of overlay) {
    if (l?.Foto) continue;
    if (l?.PerguntaId == null) continue;
    if (String(l?.Resposta ?? "").trim() === "") continue;
    substituidas.add(String(l.PerguntaId));
  }
  if (substituidas.size === 0) return base;

  const mantidas = base.filter((r) => {
    const pid = String(
      r?.perguntaId ?? r?.PerguntaId ?? r?.id_inspecao_cadastro_checklist ?? "",
    );
    return !substituidas.has(pid) || ehLinhaDeMidia(r);
  });

  const novas = overlay
    .filter(
      (l) => l?.PerguntaId != null && substituidas.has(String(l.PerguntaId)),
    )
    .map((l) => ({
      perguntaId: Number(l.PerguntaId),
      resposta: String(l.Resposta ?? ""),
    }));

  return [...mantidas, ...novas];
}

/**
 * Respostas gravadas → valores iniciais do sheet, para reabrir uma execução em modo
 * leitura ou edição.
 *
 * ⚠️ Uma pergunta pode ter VÁRIAS linhas de resposta. Foto é o caso óbvio: cada foto
 * anexada vira uma linha em `tab_adm_inspecoes_respostas` com o MESMO
 * `id_inspecao_cadastro_checklist` e a URL em `texto_resposta` (é assim que o app envia
 * e como o TirvuAdm grava). Por isso aqui as linhas são AGRUPADAS por pergunta — a versão
 * antiga fazia `initial[pid] = ...` e a segunda linha simplesmente sobrescrevia a
 * primeira, então uma pergunta com 2 fotos só exibia 1.
 *
 * Com o `schema` em mãos, cada valor volta no formato que o campo espera (fotos como
 * ChecklistAsset[], assinatura/PDF como objeto, lista múltipla como array). Sem o schema
 * há um fallback por heurística, mantido só para chamadas antigas.
 */
export function valuesDeRespostas(
  respostas: any[] | null | undefined,
  schema?: ChecklistSchema | null,
): ChecklistValues {
  // pergunta → textos (na ordem em que vieram) e a foto da coluna `foto` (LSP).
  const textosPorPergunta = new Map<string, string[]>();
  const fotoPorPergunta = new Map<string, string>();

  for (const r of Array.isArray(respostas) ? respostas : []) {
    const pidRaw =
      r?.perguntaId ?? r?.PerguntaId ?? r?.id_inspecao_cadastro_checklist;
    if (pidRaw == null) continue;
    const pid = String(pidRaw);

    const foto = String(r?.foto ?? r?.Foto ?? "").trim();
    if (foto && !fotoPorPergunta.has(pid)) fotoPorPergunta.set(pid, foto);

    const texto = String(
      r?.resposta ?? r?.Resposta ?? r?.texto_resposta ?? "",
    ).trim();
    if (!texto) continue;
    const lista = textosPorPergunta.get(pid) ?? [];
    lista.push(texto);
    textosPorPergunta.set(pid, lista);
  }

  const initial: ChecklistValues = {};
  const campos = camposDoSchema(schema ?? null);

  // Sem schema: heurística. Só o essencial — várias linhas viram lista de assets se
  // parecerem arquivo, senão juntam-se em texto.
  if (campos.length === 0) {
    for (const [pid, textos] of textosPorPergunta) {
      const foto = fotoPorPergunta.get(pid);
      if (textos.length > 1 && textos.every((t) => ehUriDeArquivo(t))) {
        initial[pid] = textos.map((uri, i) => assetDeUri(pid, i, uri));
      } else {
        initial[pid] = foto ? String(foto) : textos.join(", ");
      }
    }
    for (const [pid, foto] of fotoPorPergunta) {
      if (!textosPorPergunta.has(pid)) initial[pid] = foto;
    }
    return initial;
  }

  for (const campo of campos) {
    const pid = String(campo.id);
    const textos = textosPorPergunta.get(pid) ?? [];
    const fotoColuna = fotoPorPergunta.get(pid);

    if (campo.type === "lsp") {
      // LSP: o texto é a PLACA; a foto vem na coluna `foto` e o sheet a lê de
      // "__lspfoto_<id>" (mesma chave que a captura usa).
      if (textos.length > 0) initial[pid] = textos[0];
      if (fotoColuna) initial[`__lspfoto_${pid}`] = fotoColuna;
      continue;
    }

    if (textos.length === 0) {
      // Pergunta sem texto mas com foto gravada (LSP sem placa, por exemplo).
      if (fotoColuna) initial[pid] = fotoColuna;
      continue;
    }

    if (campo.type === "foto") {
      // O gravado é a URL em texto_resposta. Mas o payload que o app envia usa
      // {Resposta:"Foto", Foto:url} — se a origem for essa, o texto é rótulo, não imagem:
      // fica só o que parece arquivo (e, se nada parecer, a coluna `foto`).
      const uris = textos.filter((t) => ehUriDeArquivo(t));
      const fonte = uris.length > 0 ? uris : fotoColuna ? [fotoColuna] : [];
      if (fonte.length === 0) continue;
      initial[pid] = fonte.map((uri, i) => assetDeUri(pid, i, uri));
    } else if (campo.type === "assinatura") {
      initial[pid] = { signedAt: "—", uri: textos[textos.length - 1] };
    } else if (campo.type === "pdf") {
      const uri = textos[0];
      const nome = uri.split("/").pop() || "Documento PDF";
      initial[pid] = {
        id: `chk-${pid}-pdf`,
        uri,
        name: nome.includes("%") ? decodeURIComponent(nome) : nome,
      };
    } else if (campo.type === "lista_multipla") {
      const partes = textos.flatMap((t) =>
        t
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      );
      initial[pid] = [...new Set(partes)];
    } else {
      initial[pid] = textos.length === 1 ? textos[0] : textos.join(", ");
    }
  }

  return initial;
}

/** URL/arquivo? Usado só no fallback sem schema do valuesDeRespostas. */
function ehUriDeArquivo(v: string): boolean {
  return /^(https?:|file:|content:|data:)/i.test(v.trim());
}

/** Linha de resposta (URL) → asset que o campo de foto do sheet renderiza. */
function assetDeUri(pid: string, indice: number, uri: string) {
  const limpo = uri.trim();
  return { id: `chk-${pid}-${indice}-${limpo.slice(-24)}`, uri: limpo };
}
